/**
 * Exportación a CSV de la lista de propiedades (workflow del inversionista:
 * bajar los resultados filtrados a Excel/Sheets). Pure + sin dependencias
 * para que sea testeable y liviano.
 */

import type { PropertyListItem } from './types';
import { ESTADO_NAMES, humanize } from './filters';

interface Column {
  header: string;
  value: (p: PropertyListItem) => string | number | boolean | null;
}

const COLUMNS: Column[] = [
  { header: 'id', value: (p) => p.id },
  { header: 'direccion', value: (p) => p.address_normalized },
  { header: 'tipo', value: (p) => humanize('propertyType', p.property_type) },
  { header: 'estado', value: (p) => (p.cve_ent ? ESTADO_NAMES[p.cve_ent] ?? p.cve_ent : null) },
  { header: 'municipio', value: (p) => p.municipio },
  { header: 'canal', value: (p) => humanize('channel', p.legal_channel) },
  { header: 'estatus', value: (p) => humanize('status', p.acquisition_status) },
  { header: 'precio_mxn', value: (p) => p.asking_price },
  { header: 'avaluo_mxn', value: (p) => p.avaluo_price },
  { header: 'descuento_vs_avaluo_pct', value: (p) => p.opp_discount_vs_avaluo_pct },
  {
    header: 'opportunity_score',
    value: (p) => (p.opportunity_score !== null ? Math.round(p.opportunity_score) : null),
  },
  { header: 'precio_m2', value: (p) => p.opp_price_m2 },
  { header: 'm2_construido', value: (p) => p.surface_built_m2 },
  { header: 'm2_terreno', value: (p) => p.surface_terrain_m2 },
  { header: 'recamaras', value: (p) => p.bedrooms },
  { header: 'banos', value: (p) => p.bathrooms },
  { header: 'plusvalia_shf_yoy_pct', value: (p) => p.shf_yoy_pct },
  { header: 'ultima_baja_pct', value: (p) => p.last_price_change_pct },
  { header: 'ultima_baja_fecha', value: (p) => p.last_price_change_at?.slice(0, 10) ?? null },
  { header: 'libre_gravamen', value: (p) => boolToText(p.opp_is_libre_gravamen) },
  { header: 'litigio', value: (p) => boolToText(p.opp_has_litigio) },
  { header: 'zona_riesgo', value: (p) => boolToText(p.zona_riesgo_flag) },
  { header: 'riesgo_sismico', value: (p) => p.cenapred_seismic_zone },
  { header: 'riesgo_inundacion', value: (p) => p.cenapred_flood_risk },
  // Deep-link al drawer de la propiedad (feature ?property= ya en prod).
  { header: 'enlace', value: (p) => `${window.location.origin}/?property=${p.id}` },
];

function boolToText(v: boolean | null): string | null {
  if (v === null) return null;
  return v ? 'si' : 'no';
}

function escapeCell(v: string | number | boolean | null): string {
  if (v === null || v === undefined) return '';
  const s = String(v);
  // Entrecomilla si hay separadores o saltos; duplica comillas internas.
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

/**
 * Serializa propiedades a CSV. Prefija un BOM UTF-8 para que Excel
 * interprete acentos correctamente, y usa CRLF (convención CSV/Excel).
 */
export function propertiesToCsv(items: PropertyListItem[]): string {
  const head = COLUMNS.map((c) => c.header).join(',');
  const rows = items.map((p) => COLUMNS.map((c) => escapeCell(c.value(p))).join(','));
  return '\uFEFF' + [head, ...rows].join('\r\n');
}

/** Dispara la descarga de un CSV en el browser via Blob + <a download>. */
export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
